(function (global) {
  'use strict';

  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const amount = (value) => { const parsed = Number(value); return Number.isFinite(parsed) ? parsed : 0; };
  const money = (value) => Math.round(amount(value) * 100) / 100;
  const dateKey = (value) => String(value || '').slice(0, 10);
  const termForDate = (value) => Math.ceil(Number(dateKey(value).slice(5, 7)) / 2);
  const iso = (date) => date.toISOString().slice(0, 10);
  const addDays = (date, days) => new Date(date.getTime() + days * 86400000);

  const easterSunday = (year) => {
    const a = year % 19; const b = Math.floor(year / 100); const c = year % 100;
    const d = Math.floor(b / 4); const e = b % 4; const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3); const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4); const k = c % 4; const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    return new Date(Date.UTC(year, month - 1, ((h + l - 7 * m + 114) % 31) + 1));
  };
  const norwegianHolidayKeys = (year) => {
    const easter = easterSunday(year);
    return new Set([
      `${year}-01-01`, `${year}-05-01`, `${year}-05-17`, `${year}-12-25`, `${year}-12-26`,
      iso(addDays(easter, -3)), iso(addDays(easter, -2)), iso(addDays(easter, 1)),
      iso(addDays(easter, 39)), iso(addDays(easter, 50)),
    ]);
  };
  const isNorwegianWorkingDay = (value) => {
    const key = dateKey(value); const date = new Date(`${key}T00:00:00.000Z`);
    if (Number.isNaN(date.getTime())) return false;
    return ![0, 6].includes(date.getUTCDay()) && !norwegianHolidayKeys(date.getUTCFullYear()).has(key);
  };
  const nextNorwegianWorkingDay = (value, options = {}) => {
    const start = new Date(`${dateKey(value)}T00:00:00.000Z`);
    if (Number.isNaN(start.getTime())) return '';
    let candidate = options.includeDate ? start : addDays(start, 1);
    while (!isNorwegianWorkingDay(iso(candidate))) candidate = addDays(candidate, 1);
    return iso(candidate);
  };
  const withholdingDueDate = (paymentDate) => {
    const key = dateKey(paymentDate);
    if (!key || Number.isNaN(new Date(`${key}T00:00:00.000Z`).getTime())) return '';
    return key < '2026-01-01' ? agaTermDueDate(Number(key.slice(0, 4)), termForDate(key)) : nextNorwegianWorkingDay(key);
  };
  const agaTermDueDate = (year, term) => {
    const due = { 1: `${year}-03-15`, 2: `${year}-05-15`, 3: `${year}-07-15`, 4: `${year}-09-15`, 5: `${year}-11-15`, 6: `${Number(year) + 1}-01-15` }[Number(term)];
    return due ? nextNorwegianWorkingDay(due, { includeDate: true }) : '';
  };
  const runWithholding = (run) => safeArray(run?.lines).reduce((sum, line) => sum + Math.max(0, amount(line?.taxDeduction)), 0);
  const runSalaryAga = (run) => {
    const rows = safeArray(run?.payrollPostingRows);
    if (rows.length) return Math.max(0, rows.filter((row) => String(row?.accountCode || '') === '2770').reduce((sum, row) => sum + amount(row?.credit) - amount(row?.debit), 0));
    return safeArray(run?.lines).reduce((sum, line) => sum + Math.max(0, amount(line?.employerContribution)), 0);
  };
  const runPensionAga = (run) => Math.max(0, money(run?.paymentStatus?.pensionEmployerContributionAmount));
  const activeRuns = (state) => safeArray(state?.payrollRuns).filter((run) => ['approved', 'paid'].includes(run?.status));
  const agaForTerm = (state, year, term) => money(activeRuns(state).reduce((sum, run) => {
    const salaryDate = dateKey(run?.paymentDate); const pensionDate = dateKey(run?.paymentStatus?.pensionPaidAt);
    const salary = Number(salaryDate.slice(0, 4)) === Number(year) && termForDate(salaryDate) === Number(term) ? runSalaryAga(run) : 0;
    const pension = Number(pensionDate.slice(0, 4)) === Number(year) && termForDate(pensionDate) === Number(term) ? runPensionAga(run) : 0;
    return sum + salary + pension;
  }, 0) + safeArray(state?.pensionPremiumPayments).filter(payment => payment.status !== 'reversed' && Number(dateKey(payment.date).slice(0, 4)) === Number(year) && termForDate(payment.date) === Number(term)).reduce((sum, payment) => sum + Math.max(0, amount(payment.employerContribution)), 0));
  const withholdingObligations = (state, year) => activeRuns(state)
    .filter((run) => Number(dateKey(run?.paymentDate).slice(0, 4)) === Number(year) && runWithholding(run) > 0.005)
    .map((run) => ({ id: `withholding-${run.id}`, kind: 'withholding', runId: run.id, reportingYear: Number(year), paymentDate: dateKey(run.paymentDate), dueDate: withholdingDueDate(run.paymentDate), amount: runWithholding(run) }));
  const agaTermObligations = (state, year) => [1, 2, 3, 4, 5, 6]
    .map((term) => ({ id: `aga-${year}-${term}`, kind: 'aga', reportingYear: Number(year), term, dueDate: agaTermDueDate(year, term), amount: agaForTerm(state, year, term) }))
    .filter((entry) => entry.amount > 0.005);
  const employeePayment = (run, employeeId) => {
    const payment = safeArray(run?.paymentStatus?.netPayPayments).find((entry) => String(entry?.employeeId || '') === String(employeeId || ''));
    const paidAt = String(payment?.paidAt || (run?.status === 'paid' ? (run?.paymentStatus?.netPayPaidAt || run?.paidAt || '') : ''));
    return { paidAt, paymentStatus: paidAt ? 'paid' : 'ready', amount: amount(payment?.amount) };
  };
  const runPaymentSummary = (run) => {
    const lines = safeArray(run?.lines); const payments = safeArray(run?.paymentStatus?.netPayPayments);
    const paidIds = new Set(payments.map((payment) => String(payment?.employeeId || '')).filter(Boolean));
    const outstanding = run?.status === 'paid' ? [] : lines.filter((line) => amount(line?.netPay) > 0 && !paidIds.has(String(line?.employeeId || '')));
    const dates = payments.map((payment) => String(payment?.paidAt || '')).filter(Boolean).sort();
    const paidNet = run?.status === 'paid' && !payments.length ? lines.reduce((sum, line) => sum + amount(line?.netPay), 0) : payments.reduce((sum, payment) => sum + amount(payment?.amount), 0);
    return { paidNet: money(paidNet), outstandingNet: money(outstanding.reduce((sum, line) => sum + amount(line?.netPay), 0)), outstandingEmployeeIds: outstanding.map((line) => line?.employeeId).filter(Boolean), employeePayments: payments, actualPaidAt: dates[dates.length - 1] || (run?.status === 'paid' ? String(run?.paymentStatus?.netPayPaidAt || run?.paidAt || '') : '') };
  };
  const dashboardSummary = (rows) => {
    const postedRuns = safeArray(rows).filter((run) => run?.status === 'godkjent' || run?.status === 'utbetalt');
    const readyRuns = safeArray(rows).filter((run) => run?.status === 'godkjent');
    return { postedRuns, gross: money(postedRuns.reduce((sum, run) => sum + amount(run?.gross), 0)), tax: money(postedRuns.reduce((sum, run) => sum + amount(run?.tax), 0)), outstandingNet: money(readyRuns.reduce((sum, run) => sum + amount(run?.outstandingNet ?? run?.net), 0)), outstandingEmployeeCount: new Set(readyRuns.flatMap((run) => safeArray(run?.outstandingEmployeeIds)).map(String).filter(Boolean)).size };
  };

  global.PlummePayrollObligations = {
    isNorwegianWorkingDay, nextNorwegianWorkingDay, withholdingDueDate, agaTermDueDate,
    runWithholding, runSalaryAga, runPensionAga, agaForTerm, withholdingObligations, agaTermObligations,
    employeePayment, runPaymentSummary, dashboardSummary,
  };
})(window);
